import { useState, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { api } from '../lib/api';
import {
  useCalendars,
  useUpdateCalendar,
  useDeleteCalendar,
  useLeaveMembership,
  type CalendarWithRole,
} from '../hooks/useCalendars';
import {
  useSubscriptions,
  useSubscribers,
  useCreateSubscription,
  useDeleteSubscription,
  useUpdateSubscription,
} from '../hooks/useSubscriptions';
import ColorPicker from '../components/ColorPicker';
import CalendarToggleSwitch from '../components/CalendarToggleSwitch';
import { TIMEZONES } from '../lib/calendarFormConstants';
import PlanBadge from '../components/PlanBadge';
import { useCopyText } from '../hooks/useCopyText';
import { useJoinUrl } from '../hooks/useJoinUrl';

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me.get(),
  });
  const { data: billingData } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => api.billing.status(),
  });
  const { data: calendars = [] } = useCalendars();

  const user = meData?.user;
  const personalCalendarId = user?.personal_calendar_id;

  const [editingDisplayName, setEditingDisplayName] =
    useState(false);
  const [displayNameDraft, setDisplayNameDraft] =
    useState('');

  // Handle hash fragment for anchor links
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash === '#timezone') {
        const element = document.getElementById('timezone');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
          // Add a brief highlight effect
          element.classList.add(
            'ring-2',
            'ring-accent',
            'ring-offset-2'
          );
          setTimeout(() => {
            element.classList.remove(
              'ring-2',
              'ring-accent',
              'ring-offset-2'
            );
          }, 2000);
        }
      }
    };

    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () =>
      window.removeEventListener('hashchange', handleHash);
  }, []);

  const updateDisplayName = useMutation({
    mutationFn: (name: string) =>
      api.me.update({ display_name: name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      setEditingDisplayName(false);
    },
  });

  function handleDisplayNameSave() {
    const trimmed = displayNameDraft.trim();
    if (trimmed && trimmed !== (user?.display_name ?? '')) {
      updateDisplayName.mutate(trimmed);
    } else {
      setEditingDisplayName(false);
    }
  }

  const updateHolidayRegion = useMutation({
    mutationFn: (region: string | null) =>
      api.me.update({ holiday_region: region }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  // Common country codes for holidays
  const holidayRegions = [
    { code: null, name: 'Disabled' },
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'JP', name: 'Japan' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'IN', name: 'India' },
    { code: 'CN', name: 'China' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SE', name: 'Sweden' },
    { code: 'NO', name: 'Norway' },
    { code: 'DK', name: 'Denmark' },
    { code: 'FI', name: 'Finland' },
    { code: 'IE', name: 'Ireland' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'SG', name: 'Singapore' },
    { code: 'ZA', name: 'South Africa' },
  ];

  // Derive selected calendar from URL param, falling back to personal
  const urlCalendarId = searchParams.get('calendar');
  const selectedCalendarId =
    urlCalendarId ??
    personalCalendarId ??
    calendars[0]?.id ??
    '';
  const selectedCalendar = calendars.find(
    c => c.id === selectedCalendarId
  );

  function selectCalendar(id: string) {
    navigate(`/settings?calendar=${id}`, { replace: true });
  }

  return (
    <div className="p-6 max-w-2xl mx-auto w-full">
      <h2 className="font-serif text-2xl font-bold text-ink-900 mb-6">
        Settings
      </h2>

      <section className="paper-card p-5 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-widest text-ink-400 mb-3">
          Account
        </h3>
        <dl className="space-y-2">
          <div className="flex gap-4">
            <dt className="font-mono text-sm text-ink-400 w-28 shrink-0">
              Email
            </dt>
            <dd className="font-mono text-sm text-ink-900">
              {user?.email ?? '—'}
            </dd>
          </div>
          <div className="flex gap-4">
            <dt className="font-mono text-sm text-ink-400 w-28 shrink-0">
              Display name
            </dt>
            <dd className="font-mono text-sm text-ink-900 flex-1">
              {editingDisplayName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    autoFocus
                    maxLength={100}
                    className="input-field flex-1"
                    value={displayNameDraft}
                    onChange={e =>
                      setDisplayNameDraft(e.target.value)
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter')
                        handleDisplayNameSave();
                      if (e.key === 'Escape')
                        setEditingDisplayName(false);
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleDisplayNameSave}
                    disabled={updateDisplayName.isPending}
                    className="btn-primary text-xs px-2 py-1"
                  >
                    {updateDisplayName.isPending
                      ? 'Saving…'
                      : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEditingDisplayName(false)
                    }
                    className="btn-ghost text-xs px-2 py-1"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <span className="inline-flex items-center gap-2">
                  {user?.display_name || '—'}
                  <button
                    type="button"
                    onClick={() => {
                      setDisplayNameDraft(
                        user?.display_name ?? ''
                      );
                      setEditingDisplayName(true);
                    }}
                    className="font-mono text-xs text-ink-400 hover:text-ink-700 underline transition-colors"
                  >
                    edit
                  </button>
                </span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      {/* Holiday Region Section */}
      <section className="paper-card p-5 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-widest text-ink-400 mb-3">
          Holidays
        </h3>
        <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
          Show Holidays For
        </label>
        <select
          value={user?.holiday_region ?? ''}
          onChange={e =>
            updateHolidayRegion.mutate(
              e.target.value || null
            )
          }
          disabled={updateHolidayRegion.isPending}
          className="w-full px-3 py-2 rounded-sm border border-paper-300 bg-paper-100 font-mono text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent/30"
        >
          {holidayRegions.map(region => (
            <option
              key={region.code ?? 'disabled'}
              value={region.code ?? ''}
            >
              {region.name}
            </option>
          ))}
        </select>
        <p className="mt-2 font-mono text-xs text-ink-400">
          Holidays appear subtly at the bottom of calendar
          day cells.
        </p>
      </section>

      {/* Timezone */}
      <section
        className="paper-card p-5 mb-4"
        id="timezone"
      >
        <h3 className="font-mono text-xs uppercase tracking-widest text-ink-400 mb-3">
          Timezone
        </h3>

        {/* Calendar selector for timezone */}
        <div className="mb-4">
          <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
            Select Calendar
          </label>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {calendars.map(cal => (
              <button
                key={cal.id}
                type="button"
                onClick={() => selectCalendar(cal.id)}
                className={[
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm font-mono text-left transition-colors',
                  selectedCalendarId === cal.id
                    ? 'bg-paper-200 text-ink-900 font-semibold'
                    : 'text-ink-700 hover:bg-paper-100',
                ].join(' ')}
              >
                <span
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{ backgroundColor: cal.color }}
                />
                <span className="truncate">{cal.name}</span>
                {cal.is_personal && (
                  <span className="ml-auto text-ink-200 text-xs">
                    you
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {selectedCalendar && (
          <div className="pt-3 border-t border-paper-300">
            <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
              Timezone for {selectedCalendar.name}
            </label>
            <TimezoneSetting calendar={selectedCalendar} />
          </div>
        )}
      </section>

      {/* Calendar relationship — plain members only */}
      {selectedCalendar &&
        !selectedCalendar.is_personal &&
        personalCalendarId &&
        selectedCalendar.role !== 'owner' &&
        selectedCalendar.role !== 'manager' && (
          <section className="paper-card p-5 mb-4">
            <div className="mb-3">
              <div className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{
                    backgroundColor: selectedCalendar.color,
                  }}
                />
                <h3 className="font-mono text-sm font-semibold text-ink-900">
                  {selectedCalendar.name}
                </h3>
              </div>
              <p className="font-mono text-xs text-ink-400 mt-0.5 ml-4">
                {selectedCalendar.is_subscribed &&
                selectedCalendar.is_member
                  ? 'member · subscribed'
                  : selectedCalendar.is_subscribed
                    ? 'subscriber'
                    : selectedCalendar.is_member
                      ? 'member'
                      : 'guest'}
              </p>
            </div>
            <CalendarRelationshipForm
              calendar={selectedCalendar}
              personalCalendarId={personalCalendarId}
            />
          </section>
        )}

      {/* Calendar Settings Section */}
      <section className="paper-card p-5 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-widest text-ink-400 mb-3">
          Calendar Settings
        </h3>

        {selectedCalendar && (
          <CalendarSettingsForm
            calendar={selectedCalendar}
          />
        )}
      </section>

      {/* Billing Section */}
      <section className="paper-card p-5 mb-4">
        <h3 className="font-mono text-xs uppercase tracking-widest text-ink-400 mb-4">
          Billing
        </h3>

        {billingData ? (
          <div className="space-y-4">
            {/* Plan Status */}
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-ink-400">
                Current Plan
              </span>
              <PlanBadge
                status={billingData.plan.status as any}
                tier={billingData.plan.tier}
                trialEndsAt={billingData.plan.trial_ends_at}
              />
            </div>

            {/* Calendar limit */}
            {billingData.plan.calendar_limit > 0 && (
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-ink-400">
                  Shared Calendars
                </span>
                <span className="font-mono text-sm text-ink-900">
                  up to {billingData.plan.calendar_limit}
                </span>
              </div>
            )}

            {/* Trial countdown (if applicable) */}
            {billingData.plan.status === 'trial' &&
              billingData.plan.trial_ends_at && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-800">
                    Your free trial ends on{' '}
                    <strong>
                      {new Date(
                        billingData.plan.trial_ends_at
                      ).toLocaleDateString()}
                    </strong>
                    . Subscribe before then to keep your
                    calendars active.
                  </p>
                </div>
              )}

            {/* Action buttons */}
            <div className="flex flex-col gap-2 pt-2">
              {billingData.plan.is_paid ? (
                <button
                  onClick={async () => {
                    try {
                      const { url } =
                        await api.billing.portal();
                      window.location.href = url;
                    } catch {
                      alert(
                        'Failed to open billing portal'
                      );
                    }
                  }}
                  className="btn-ghost"
                >
                  Manage Billing
                </button>
              ) : billingData.plan.status !== 'active' ? (
                <>
                  <p className="text-sm text-ink-600 mb-1">
                    Choose a plan to continue:
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        tier: 'starter' as const,
                        label: 'Starter',
                        price: '$1',
                        calendars: 1,
                      },
                      {
                        tier: 'standard' as const,
                        label: 'Standard',
                        price: '$2',
                        calendars: 3,
                      },
                      {
                        tier: 'pro' as const,
                        label: 'Pro',
                        price: '$3',
                        calendars: 10,
                      },
                    ].map(
                      ({
                        tier,
                        label,
                        price,
                        calendars,
                      }) => (
                        <button
                          key={tier}
                          onClick={async () => {
                            try {
                              const { url } =
                                await api.billing.checkout(
                                  tier
                                );
                              window.location.href = url;
                            } catch {
                              alert(
                                'Failed to start checkout'
                              );
                            }
                          }}
                          className="flex flex-col items-center gap-1 p-2 rounded border border-paper-300 hover:border-accent hover:bg-paper-100 transition-colors text-sm"
                        >
                          <span className="font-medium text-ink-900">
                            {label}
                          </span>
                          <span className="font-mono text-xs text-ink-500">
                            {price}/mo
                          </span>
                          <span className="font-mono text-xs text-ink-500">
                            {calendars} cal
                          </span>
                        </button>
                      )
                    )}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="font-mono text-sm text-ink-400">
            Loading billing information...
          </p>
        )}
      </section>

      <section className="paper-card p-5">
        <h3 className="font-mono text-xs uppercase tracking-widest text-ink-400 mb-3">
          About
        </h3>
        <p className="font-mono text-sm text-ink-400">
          CommonCal v0.1.0 — shared group calendar
        </p>
      </section>
    </div>
  );
}

// Standalone timezone setting that saves immediately on change
function TimezoneSetting({
  calendar,
}: {
  calendar: CalendarWithRole;
}) {
  const { mutateAsync: updateCalendar } =
    useUpdateCalendar();
  const [timezone, setTimezone] = useState(
    calendar.timezone
  );
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setTimezone(calendar.timezone);
  }, [calendar.id, calendar.timezone]);

  const isOwner = calendar.role === 'owner';

  async function handleTimezoneChange(newTimezone: string) {
    if (!isOwner) return;
    setTimezone(newTimezone);
    try {
      await updateCalendar({
        id: calendar.id,
        timezone: newTimezone,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch {
      // Revert on error
      setTimezone(calendar.timezone);
    }
  }

  return (
    <div className="space-y-2">
      <select
        className="input-field"
        value={timezone}
        onChange={e => handleTimezoneChange(e.target.value)}
        disabled={!isOwner}
      >
        {TIMEZONES.map(tz => (
          <option key={tz} value={tz}>
            {tz}
          </option>
        ))}
      </select>
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs text-ink-400">
          All times are displayed in this timezone.
        </p>
        {saved && (
          <span className="font-mono text-xs text-green-600">
            Saved!
          </span>
        )}
      </div>
    </div>
  );
}

function CalendarRelationshipForm({
  calendar,
  personalCalendarId,
}: {
  calendar: CalendarWithRole;
  personalCalendarId: string;
}) {
  const navigate = useNavigate();
  const createSubscription = useCreateSubscription();
  const leaveMembership = useLeaveMembership();

  const { data: personalSubs } = useSubscriptions(
    personalCalendarId
  );
  const existingSubscription = personalSubs?.find(
    s => s.source_calendar_id === calendar.id
  );
  const deleteSubscription = useDeleteSubscription(
    personalCalendarId
  );
  const updateSubscription = useUpdateSubscription(
    personalCalendarId
  );

  const isMember = Boolean(calendar.is_member);
  const isSubscribed =
    Boolean(calendar.is_subscribed) ||
    Boolean(existingSubscription);

  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmUnsubscribe, setConfirmUnsubscribe] =
    useState(false);
  const [colorOverride, setColorOverride] = useState<
    string | null
  >(existingSubscription?.color_override ?? null);
  const [membershipError, setMembershipError] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setColorOverride(
      existingSubscription?.color_override ?? null
    );
  }, [calendar.id, existingSubscription?.color_override]);

  async function handleSubscribe() {
    if (!calendar.join_token) return;
    try {
      await createSubscription.mutateAsync({
        join_token: calendar.join_token,
        subscriber_calendar_id: personalCalendarId,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Subscribe failed'
      );
    }
  }

  async function handleUnsubscribe() {
    if (!existingSubscription) return;
    try {
      await deleteSubscription.mutateAsync(
        existingSubscription.id
      );
      setConfirmUnsubscribe(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unsubscribe failed'
      );
    }
  }

  async function handleLeave() {
    setMembershipError(null);
    try {
      await leaveMembership.mutateAsync(calendar.id);
      navigate('/calendar');
    } catch (err) {
      const code = err instanceof Error ? err.message : '';
      if (code === 'must_unsubscribe_first') {
        setMembershipError(
          'You must unsubscribe before leaving this calendar.'
        );
      } else {
        setMembershipError(
          err instanceof Error
            ? err.message
            : 'Leave failed'
        );
      }
      setConfirmLeave(false);
    }
  }

  return (
    <div className="space-y-3">
      {error && (
        <p className="font-mono text-xs text-red-600">
          {error}
        </p>
      )}

      {/* Subscribe action (member-only, not yet subscribed) */}
      {isMember && !isSubscribed && (
        <button
          type="button"
          onClick={handleSubscribe}
          disabled={createSubscription.isPending}
          className="btn-primary w-full text-sm disabled:opacity-40"
        >
          {createSubscription.isPending
            ? 'Subscribing…'
            : 'Subscribe — copy events to my calendar'}
        </button>
      )}

      {/* Color override for subscribed calendars */}
      {isSubscribed && existingSubscription && (
        <div>
          <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
            Display color
          </label>
          <div className="flex items-center gap-2">
            <ColorPicker
              value={colorOverride ?? calendar.color}
              onChange={setColorOverride}
            />
            {colorOverride && (
              <button
                type="button"
                onClick={() => setColorOverride(null)}
                className="px-2 py-1.5 text-xs font-mono text-ink-500 hover:text-ink-700 transition-colors whitespace-nowrap"
              >
                Reset to default
              </button>
            )}
          </div>
          <p className="mt-1 font-mono text-[10px] text-ink-300">
            {colorOverride
              ? 'Using custom display color'
              : "Using the calendar's original color"}
          </p>
          <button
            type="button"
            onClick={() =>
              updateSubscription.mutate({
                id: existingSubscription.id,
                color_override: colorOverride,
              })
            }
            disabled={
              updateSubscription.isPending ||
              colorOverride ===
                (existingSubscription.color_override ??
                  null)
            }
            className="mt-2 btn-primary text-sm w-full disabled:opacity-40"
          >
            {updateSubscription.isPending
              ? 'Saving…'
              : 'Save color'}
          </button>
        </div>
      )}

      {/* Unsubscribe action */}
      {isSubscribed && (
        <div>
          {confirmUnsubscribe ? (
            <div className="space-y-2">
              <p className="font-mono text-xs text-red-600 text-center">
                Unsubscribe? You will no longer see this
                calendar's events on your personal view.
                {isMember
                  ? ' The calendar will still appear in your sidebar.'
                  : ' The calendar will leave your sidebar.'}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setConfirmUnsubscribe(false)
                  }
                  className="btn-ghost flex-1 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUnsubscribe}
                  disabled={deleteSubscription.isPending}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-mono hover:bg-red-700 transition-colors disabled:opacity-40"
                >
                  {deleteSubscription.isPending
                    ? 'Unsubscribing…'
                    : 'Unsubscribe'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmUnsubscribe(true)}
              className="w-full font-mono text-xs text-red-500 hover:text-red-700 py-1.5 transition-colors"
            >
              Unsubscribe from calendar
            </button>
          )}
        </div>
      )}

      {/* Leave membership action */}
      {isMember && (
        <div>
          {membershipError && (
            <p className="font-mono text-xs text-red-600 mb-2">
              {membershipError}
            </p>
          )}
          {confirmLeave ? (
            <div className="space-y-2">
              <p className="font-mono text-xs text-amber-700 text-center">
                Leave this calendar? It will be removed from
                your sidebar. You will need a new join link
                to rejoin.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmLeave(false);
                    setMembershipError(null);
                  }}
                  className="btn-ghost flex-1 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleLeave}
                  disabled={leaveMembership.isPending}
                  className="flex-1 bg-amber-600 text-white px-4 py-2 rounded-sm text-sm font-mono hover:bg-amber-700 transition-colors disabled:opacity-40"
                >
                  {leaveMembership.isPending
                    ? 'Leaving…'
                    : 'Leave calendar'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (isSubscribed) {
                  setMembershipError(
                    'You must unsubscribe before leaving this calendar.'
                  );
                  return;
                }
                setConfirmLeave(true);
              }}
              className="w-full font-mono text-xs text-amber-600 hover:text-amber-800 py-1.5 transition-colors"
            >
              Leave calendar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function CalendarSettingsForm({
  calendar,
}: {
  calendar: CalendarWithRole;
}) {
  const navigate = useNavigate();
  const { copyToClipboard, copied } = useCopyText();
  const {
    mutateAsync: updateCalendar,
    isPending: isUpdating,
  } = useUpdateCalendar();
  const {
    mutateAsync: deleteCalendar,
    isPending: isDeleting,
  } = useDeleteCalendar();

  const { data: memberData } = useQuery({
    queryKey: ['calendars', calendar.id, 'members'],
    queryFn: () => api.calendars.members(calendar.id),
  });

  const { data: subscriberData } = useSubscribers(
    !calendar.is_personal ? calendar.id : undefined
  );

  const [name, setName] = useState(calendar.name);
  const [color, setColor] = useState(calendar.color);
  const [description, setDescription] = useState(
    calendar.description ?? ''
  );
  const [warnOnConflicts, setWarnOnConflicts] = useState(
    calendar.warn_on_conflicts
  );
  const [isKocal, setIsKocal] = useState(calendar.is_kocal);
  const [showKocalTip, setShowKocalTip] = useState(false);
  const [showConflictTip, setShowConflictTip] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(calendar.name);
    setColor(calendar.color);
    setDescription(calendar.description ?? '');
    setWarnOnConflicts(calendar.warn_on_conflicts);
    setIsKocal(calendar.is_kocal);
    setError(null);
    setConfirmDelete(false);
    setSaved(false);
  }, [calendar.id]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await updateCalendar({
        id: calendar.id,
        name: name.trim(),
        color,
        description: description.trim() || null,
        warn_on_conflicts: warnOnConflicts,
        is_kocal: isKocal,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Save failed'
      );
    }
  }

  async function handleDelete() {
    try {
      await deleteCalendar(calendar.id);
      navigate('/calendar');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Delete failed'
      );
      setConfirmDelete(false);
    }
  }

  const isOwner = calendar.role === 'owner';
  const isManager = calendar.role === 'manager';
  const isPersonal = calendar.is_personal;

  const participants = (() => {
    type Participant = {
      user_id: string;
      display_name: string | null;
      email: string | null;
      role: 'owner' | 'manager' | 'member';
    };
    const map = new Map<string, Participant>();
    for (const m of memberData?.members ?? []) {
      map.set(m.user_id, {
        user_id: m.user_id,
        display_name: m.display_name,
        email: m.email,
        role: m.role as 'owner' | 'manager' | 'member',
      });
    }
    for (const s of subscriberData ?? []) {
      if (
        s.subscriber_user_id &&
        !map.has(s.subscriber_user_id)
      ) {
        map.set(s.subscriber_user_id, {
          user_id: s.subscriber_user_id,
          display_name: s.subscriber_display_name,
          email: s.subscriber_email,
          role: 'member',
        });
      }
    }
    return Array.from(map.values());
  })();

  const shareUrl = useJoinUrl(calendar);

  return (
    <form
      onSubmit={handleSave}
      className="space-y-4 min-w-0"
    >
      <div>
        <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          maxLength={100}
          className="input-field"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={!isOwner}
        />
      </div>

      {(isOwner || isManager) && (
        <div>
          <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
            Color
          </label>
          <ColorPicker
            value={color}
            onChange={setColor}
            disabled={!isOwner && !isManager}
          />
        </div>
      )}

      <div>
        <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
          Description
        </label>
        <textarea
          className="input-field resize-none"
          rows={2}
          maxLength={500}
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={!isOwner}
        />
      </div>

      {!isPersonal && (
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <span className="relative flex items-center gap-1">
            <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
              Enable Kocal
            </span>
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                setShowKocalTip(v => !v);
              }}
              className="text-ink-200 hover:text-ink-400 transition-colors leading-none"
              aria-label="What does this do?"
            >
              <svg
                viewBox="0 0 16 16"
                fill="currentColor"
                className="w-3.5 h-3.5"
              >
                <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm0 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-3.5H7V4h2v4.5Z" />
              </svg>
            </button>
            {showKocalTip && (
              <div className="absolute left-0 top-full mt-2 sm:top-auto sm:bottom-full sm:mt-0 sm:mb-2 w-56 bg-ink-900 text-paper-50 font-mono text-xs rounded-sm px-3 py-2 z-50 shadow-lg">
                When enabled, anyone with your calendar link
                can contribute events from their personal
                calendar.
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    setShowKocalTip(false);
                  }}
                  className="absolute top-1 right-2 text-paper-50/60 hover:text-paper-50"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            )}
          </span>
          <CalendarToggleSwitch
            checked={isKocal}
            onChange={setIsKocal}
            disabled={!isOwner}
          />
        </label>
      )}

      <label className="flex items-center gap-3 cursor-pointer select-none">
        <span className="relative flex items-center gap-1">
          <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
            Warn on conflicts
          </span>
          <button
            type="button"
            onClick={e => {
              e.preventDefault();
              setShowConflictTip(v => !v);
            }}
            className="text-ink-200 hover:text-ink-400 transition-colors leading-none"
            aria-label="What does this do?"
          >
            <svg
              viewBox="0 0 16 16"
              fill="currentColor"
              className="w-3.5 h-3.5"
            >
              <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm0 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-3.5H7V4h2v4.5Z" />
            </svg>
          </button>
          {showConflictTip && (
            <div className="absolute left-0 top-full mt-2 sm:top-auto sm:bottom-full sm:mt-0 sm:mb-2 w-56 bg-ink-900 text-paper-50 font-mono text-xs rounded-sm px-3 py-2 z-50 shadow-lg">
              When enabled, you&apos;ll see a warning if a
              new event overlaps with an existing one on
              this calendar.
              <button
                type="button"
                onClick={e => {
                  e.preventDefault();
                  setShowConflictTip(false);
                }}
                className="absolute top-1 right-2 text-paper-50/60 hover:text-paper-50"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          )}
        </span>
        <CalendarToggleSwitch
          checked={warnOnConflicts}
          onChange={setWarnOnConflicts}
          disabled={!isOwner}
        />
      </label>

      {/* Share Link — non-personal calendars only */}
      {shareUrl && (
        <div>
          <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
            Share Link
          </label>
          <div className="space-y-2">
            <div className="flex items-center gap-2 min-w-0">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="input-field flex-1 w-0 min-w-0 font-mono text-xs"
                onClick={e => e.currentTarget.select()}
              />
              <button
                type="button"
                onClick={() => copyToClipboard(shareUrl)}
                className={[
                  'px-3 py-2 text-xs font-mono rounded-sm transition-colors shrink-0',
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-paper-100 text-ink-600 hover:bg-paper-200',
                ].join(' ')}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="font-mono text-xs text-ink-300">
              Share this link to let others access this
              calendar.
              {isKocal
                ? ' They can add events directly or subscribe to see them.'
                : ' They can subscribe to see events on their personal calendar.'}
            </p>
          </div>
        </div>
      )}

      {/* Participants — owner/manager only, non-personal calendars */}
      {!isPersonal &&
        (isOwner || isManager) &&
        participants.length > 0 && (
          <div className="border-t border-paper-200 pt-4">
            <p className="font-mono text-xs text-ink-400 uppercase tracking-widest mb-2">
              Members
            </p>
            <div className="space-y-1">
              {participants.map(p => (
                <div
                  key={p.user_id}
                  className="flex items-center justify-between py-1"
                >
                  <span className="font-mono text-sm text-ink-700 truncate">
                    {p.display_name || p.email}
                  </span>
                  <span className="font-mono text-xs text-ink-400 shrink-0 ml-2">
                    {p.role}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      {error && (
        <p className="font-mono text-xs text-red-600">
          {error}
        </p>
      )}

      <div className="pt-2 space-y-2">
        {(isOwner || isManager) && (
          <button
            type="submit"
            disabled={isUpdating || !name.trim()}
            className="btn-primary w-full"
          >
            {isUpdating
              ? 'Saving\u2026'
              : saved
                ? 'Saved!'
                : 'Save changes'}
          </button>
        )}

        {isOwner && !isPersonal && (
          <>
            {confirmDelete ? (
              <div className="space-y-2">
                <p className="font-mono text-xs text-red-600 text-center">
                  Delete "{calendar.name}"? This cannot be
                  undone.
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="btn-ghost flex-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-sm text-sm font-mono hover:bg-red-700 transition-colors disabled:opacity-40"
                  >
                    {isDeleting
                      ? 'Deleting\u2026'
                      : 'Delete'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="w-full font-mono text-xs text-red-500 hover:text-red-700 py-1.5 transition-colors"
              >
                Delete calendar
              </button>
            )}
          </>
        )}
      </div>
    </form>
  );
}
